// Google Analytics 4 Implementation
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

export const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;

export const initGA = () => {
  console.log('[Analytics] Environment check:', {
    NODE_ENV: import.meta.env.MODE,
    GA_MEASUREMENT_ID: GA_MEASUREMENT_ID,
    hasWindow: typeof window !== 'undefined'
  });
  
  if (typeof window !== 'undefined' && GA_MEASUREMENT_ID) {
    console.log('[Analytics] Initializing Google Analytics 4:', GA_MEASUREMENT_ID);
    
    // Check if script is already loaded
    const existingScript = document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}"]`);
    if (existingScript) {
      console.log('[Analytics] GA script already exists, skipping initialization');
      return;
    }
    
    // Initialize dataLayer first
    window.dataLayer = window.dataLayer || [];
    function gtag(...args: any[]) {
      window.dataLayer.push(args);
    }
    window.gtag = gtag;
    
    // Load gtag script
    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    script.async = true;
    script.onload = () => {
      console.log('[Analytics] GA4 script loaded successfully');
      // Configure GA4 after script loads
      window.gtag('js', new Date());
      window.gtag('config', GA_MEASUREMENT_ID, {
        page_path: window.location.pathname,
        send_page_view: true,
      });
      console.log('[Analytics] GA4 configured for:', window.location.pathname);
      
      // Test if gtag is working
      setTimeout(() => {
        if (window.gtag) {
          console.log('[Analytics] gtag is available, sending test event');
          window.gtag('event', 'ga_initialized', {
            event_category: 'analytics',
            event_label: 'initialization_test'
          });
        }
      }, 100);
    };
    script.onerror = (error) => {
      console.error('[Analytics] Failed to load GA4 script:', error);
      console.error('[Analytics] This could be due to:');
      console.error('- Ad blockers blocking Google Analytics');
      console.error('- Network connectivity issues');
      console.error('- Content Security Policy restrictions');
      console.error('- Invalid GA measurement ID');
    };
    
    console.log('[Analytics] Adding GA script to document head');
    document.head.appendChild(script);
  } else if (typeof window !== 'undefined') {
    console.log('[Analytics] GA4 not initialized - VITE_GA_MEASUREMENT_ID not set or empty:', GA_MEASUREMENT_ID);
  }
};

// Helper function to wait for gtag to be available
const waitForGtag = (callback: () => void, maxAttempts = 10, attempt = 1) => {
  if (typeof window !== 'undefined' && window.gtag) {
    callback();
  } else if (attempt < maxAttempts) {
    setTimeout(() => waitForGtag(callback, maxAttempts, attempt + 1), 100);
  } else {
    console.log('[Analytics] gtag not available after waiting');
  }
};

export const trackPageView = (url: string) => {
  if (typeof window !== 'undefined' && GA_MEASUREMENT_ID) {
    waitForGtag(() => {
      console.log('[Analytics] Page view:', url);
      
      // Use the recommended GA4 page_view event instead of config
      window.gtag('event', 'page_view', {
        page_title: document.title,
        page_location: window.location.href,
        page_path: url,
      });
    });
  } else {
    console.log('[Analytics] Page view not tracked - GA_MEASUREMENT_ID not set');
  }
};

export const trackEvent = (eventName: string, params?: Record<string, any>) => {
  console.log('[Analytics] Event:', eventName, params);
  if (typeof window !== 'undefined' && GA_MEASUREMENT_ID) {
    waitForGtag(() => {
      window.gtag('event', eventName, params);
      console.log('[Analytics] Event sent successfully');
    });
  } else {
    console.log('[Analytics] Event not sent - GA_MEASUREMENT_ID not set');
  }
};

export const trackConversion = (conversionType: string, value?: number) => {
  trackEvent('conversion', {
    conversion_type: conversionType,
    value: value,
    currency: 'EUR',
  });
};
